import { Component, OnInit } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { LUCIDE_ICONS, LucideAngularModule, LucideIconProvider, icons } from 'lucide-angular';
import { Router, RouterModule } from '@angular/router';
import { CognitoService } from '../../services/cognito.service';
import { User } from '../../models/user';
import { AuthService } from '../../services/auth.service';
import { AuthError } from 'aws-amplify/auth';
import { NotificationService } from '../../services/notification.service';
import { CommonModule } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';


@Component({
  selector: 'app-register',
  standalone: true,
  imports: [LucideAngularModule, ReactiveFormsModule, FormsModule, RouterModule, CommonModule],
  templateUrl: './register.component.html',
  styleUrl: './register.component.scss',
  providers: [{ provide: LUCIDE_ICONS, multi: true, useValue: new LucideIconProvider(icons) }]

})
export class RegisterComponent implements OnInit {

  // Login Form
  signupForm!: UntypedFormGroup;
  loading = false;
  successmsg = false;
  error = '';
  // set the current year
  year: number = new Date().getFullYear();

  fieldTextType!: boolean;
  submitted = false;

  dropdownOpen = false; // Controla si el dropdown está abierto o cerrado
  options = ['Agente de Atención al Cliente', 'Soporte IT']; // Lista de opciones
  selectedOption = this.options[0]; // Opción seleccionada

  toggleDropdown() {
    this.dropdownOpen = !this.dropdownOpen;
  }

  selectOption(option: string) {
    this.selectedOption = option;
    this.dropdownOpen = false; // Cerrar dropdown después de seleccionar
  }

  constructor(private formBuilder: UntypedFormBuilder, public store: Store, public cognitoService: CognitoService,
    public auth: AuthService, public router: Router, private notificationService: NotificationService,
    private traslateService: TranslateService) { }

  ngOnInit(): void {
    /**
     * Form Validatyion
     */
    this.signupForm = this.formBuilder.group({
      name: ['', [Validators.required]],
      lastName: ['', [Validators.required]],
      email: ['', Validators.required, Validators.email],
      password: ['', Validators.required],
      jobPosition: ['', Validators.required],
    });
  }

  // convenience getter for easy access to form fields
  get f() { return this.signupForm.controls; }

  async onSubmit() {
    this.loading = false;
    this.submitted = true;

    const name = this.f['name'].value;
    const lastName = this.f['lastName'].value;
    const email = this.f['email'].value;
    const password = this.f['password'].value;
    const jobPosition = this.selectedOption;

    if (this.signupForm.invalid) {
      return;
    }

    if (!this.validatePass(password)) {
      const title = await this.traslateService.get('NOTIFI.titleWarning').toPromise();
      const detail = await this.traslateService.get('ERRORS.PassInvalid').toPromise();
      this.notificationService.showWarning(title, detail)
      return
    }

    const isEmail = /^[a-zA-Z0-9.!#$%&*+/=?^_`{|}~-]+@[a-zA-Z0-9~-]+(?:\.[a-zA-Z0-9-]+)*$/.test(email!.toString());
    if (isEmail) {
      this.loading = true;
      this.registerUser(name!.replace(/\s/g, ''), lastName!.replace(/\s/g, ''), email!.replace(/\s/g, ''), password!.replace(/\s/g, ''), jobPosition);
    } else {
      const title = await this.traslateService.get('NOTIFI.titleWarning').toPromise();
      const detail = await this.traslateService.get('ERRORS.emailError').toPromise();
      this.notificationService.showWarning(title, detail)
      this.loading = false;
    }

  }

  async registerUser(name: string, lastName: string, email: string, password: string, jobPosition: string) {
    try {
      localStorage.setItem("username", email);
      const user: User = {
        username: email,
        name: name,
        lastName: lastName,
        jobPosition: jobPosition,
        type: "staff"
      }
      this.auth.createUser(user).subscribe(
        async response => {
          await this.cognitoService.handleSignUp(email, password, email);
          console.log('User saved:', response);
          const title = await this.traslateService.get('NOTIFI.titleSuccess').toPromise();
          const detail = await this.traslateService.get('NOTIFI.detailRegister').toPromise();
          this.notificationService.showStandard(title, detail)
          this.router.navigateByUrl("/account-confirmation");
        },
        async (error) => {
          const errorDetail = error?.error?.detailCode || 'Error';
          const title = await this.traslateService.get('NOTIFI.titleError').toPromise();
          const detail = await this.traslateService.get(`ERRORS.${errorDetail}`).toPromise();
          this.notificationService.showError(title, detail)
        }
      );
    } catch (error) {
      if (error instanceof AuthError) {
        switch (error.name) {
          case "UsernameExistsException":
            const title = await this.traslateService.get('NOTIFI.titleSuccess').toPromise();
            const detail = await this.traslateService.get('ERRORS.registeredUserError').toPromise();
            this.notificationService.showError(title, detail)
            break
          default:
            const detailB = await this.traslateService.get(`ERRORS.Error`).toPromise();
            this.notificationService.showError(title, detailB)
            break
        }
      } else {
        const title = await this.traslateService.get('NOTIFI.titleError').toPromise();
        const detailB = await this.traslateService.get(`ERRORS.Error`).toPromise();
        this.notificationService.showError(title, detailB)
      }
      this.loading = false;
    }
  }

  validatePass(password: string): boolean {
    //validamos la fortaleza de la contra, esta funcion lo utilizamos en insertar y modificar
    const passwordStrengthPattern: RegExp = /^(?=(.*\d){2})(?=.*[a-z])(?=.*[A-Z])(?=.*[^a-zA-Z\d]).{8,}$/;
    return passwordStrengthPattern.test(password);
  }

}
